import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon, PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const KIND_LABEL = {
  closed: 'Center closed',
  paid: 'Paid holiday',
  observed: 'Observed (open)',
};
const KIND_COLOR = {
  closed: 'bg-red-100 text-red-700',
  paid: 'bg-emerald-100 text-emerald-700',
  observed: 'bg-blue-100 text-blue-700',
};

export default function HolidaysPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/holidays');
      setRows(data);
    } catch { toast.error('Failed to load holidays'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm('Delete this holiday?')) return;
    try {
      await api.delete(`/holidays/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  // Group by year for readability
  const byYear = rows.reduce((acc, r) => {
    const y = r.date?.slice(0, 4) || 'unknown';
    if (!acc[y]) acc[y] = [];
    acc[y].push(r);
    return acc;
  }, {});

  return (
    <div className="animate-slide-up">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDaysIcon className="w-6 h-6" /> Holidays & Closures
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Center closures, paid holidays, and observed days. These appear on the calendar
            and factor into payroll (paid holidays count as paid hours).
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 inline-flex items-center gap-1">
          <PlusIcon className="w-4 h-4" /> Add holiday
        </button>
      </header>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No holidays yet.
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(byYear).sort().map(([year, list]) => (
            <div key={year} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">{year}</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {list.map(r => (
                  <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-50">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xs font-mono text-gray-500 w-20 flex-shrink-0">
                        {new Date(r.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <span className="text-sm font-medium text-gray-900 flex-1 truncate">{r.name}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${KIND_COLOR[r.kind]}`}>
                        {KIND_LABEL[r.kind]}
                      </span>
                      {r.is_recurring && <span className="text-[10px] text-gray-400">recurring</span>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(r)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(r.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <HolidayModal holiday={editing === 'new' ? {} : editing}
          isNew={editing === 'new'}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function HolidayModal({ holiday, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: holiday.date?.slice(0, 10) || '',
    name: holiday.name || '',
    kind: holiday.kind || 'paid',
    notes: holiday.notes || '',
    is_recurring: !!holiday.is_recurring,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.date || !form.name) { toast.error('Date and name are required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/holidays', form);
        toast.success('Holiday added');
      } else {
        await api.patch(`/holidays/${holiday.id}`, form);
        toast.success('Updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold">{isNew ? 'Add holiday' : 'Edit holiday'}</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Date">
            <input type="date" min={DATE_FLOOR} value={form.date} onChange={(e) => setForm({...form, date: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Name">
            <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder='e.g. "Thanksgiving"'
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </Field>
          <Field label="Kind">
            <select value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white">
              <option value="paid">Paid holiday — center closed, staff paid</option>
              <option value="closed">Center closed — unpaid</option>
              <option value="observed">Observed — center open, informational</option>
            </select>
          </Field>
          <Field label="Notes (optional)">
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
              rows={2}
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm resize-y" />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_recurring}
              onChange={(e) => setForm({...form, is_recurring: e.target.checked})}
              className="accent-orange-500" />
            Repeats every year on this date
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
            <button onClick={save} disabled={saving}
              className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : (isNew ? 'Add' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
